import {
  Ban,
  CalendarClock,
  CircleCheck,
  CircleDashed,
  CircleSlash,
  Hourglass,
  Link2Off,
  Radio,
  TriangleAlert,
  Video,
  VideoOff,
  type LucideIcon,
} from 'lucide-react';
import type { VariantProps } from 'class-variance-authority';
import type { JoinWindowState, MeetingStatus, RecordingStatus } from '@hiredude/meet-shared';

import { Badge } from '@/components/ui/badge';
import type { badgeVariants } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>;

/** `MeetingView.recordingState` widens `RecordingStatus` with "never started". */
export type RecordingState = RecordingStatus | 'none';

export interface StatusMeta {
  label: string;
  variant: BadgeVariant;
  icon: LucideIcon;
}

/**
 * One source of truth for status colour, so a "live" pill on the dashboard and
 * a "live" pill in the table can never drift apart.
 */
export const MEETING_STATUS_META: Record<MeetingStatus, StatusMeta> = {
  scheduled: { label: 'Scheduled', variant: 'default', icon: CalendarClock },
  live: { label: 'Live', variant: 'success', icon: Radio },
  ended: { label: 'Ended', variant: 'outline', icon: CircleCheck },
  cancelled: { label: 'Cancelled', variant: 'destructive', icon: Ban },
};

/**
 * Join-link state, not meeting state. A scheduled or live meeting keeps its
 * links open. Ending or cancelling the interview closes every link, which is
 * the screen a candidate sees as "this interview has already finished".
 */
export const JOIN_WINDOW_META: Record<JoinWindowState, StatusMeta> = {
  open: { label: 'Links open', variant: 'success', icon: Radio },
  too_early: { label: 'Opens later', variant: 'warning', icon: Hourglass },
  too_late: { label: 'Links expired', variant: 'outline', icon: Link2Off },
  ended: { label: 'Links closed', variant: 'outline', icon: CircleCheck },
  cancelled: { label: 'Links closed', variant: 'destructive', icon: Ban },
};

export const RECORDING_STATUS_META: Record<RecordingState, StatusMeta> = {
  none: { label: 'No recording', variant: 'outline', icon: VideoOff },
  starting: { label: 'Starting', variant: 'warning', icon: CircleDashed },
  active: { label: 'Recording', variant: 'success', icon: Video },
  completed: { label: 'Recorded', variant: 'success', icon: CircleCheck },
  failed: { label: 'Failed', variant: 'destructive', icon: TriangleAlert },
  aborted: { label: 'Aborted', variant: 'warning', icon: CircleSlash },
};

/** Tailwind background for the small dots in the dashboard breakdown lists. */
export const VARIANT_DOT: Record<BadgeVariant, string> = {
  default: 'bg-primary',
  outline: 'bg-muted-foreground/60',
  success: 'bg-[var(--success)]',
  warning: 'bg-[var(--warning)]',
  destructive: 'bg-destructive',
};

export const MEETING_STATUSES: readonly MeetingStatus[] = [
  'scheduled',
  'live',
  'ended',
  'cancelled',
];

export const RECORDING_STATUSES: readonly RecordingStatus[] = [
  'starting',
  'active',
  'completed',
  'failed',
  'aborted',
];

export function isMeetingStatus(value: string): value is MeetingStatus {
  return (MEETING_STATUSES as readonly string[]).includes(value);
}

export function MeetingStatusBadge({
  status,
  className,
}: {
  status: MeetingStatus;
  className?: string;
}) {
  const { label, variant, icon: Icon } = MEETING_STATUS_META[status];

  return (
    <Badge variant={variant} className={cn('meeting-status', className)} data-status={status}>
      {status === 'live' ? (
        <span className="animate-pulse-dot size-1.5 rounded-full bg-current" aria-hidden />
      ) : (
        <Icon aria-hidden />
      )}
      {label}
    </Badge>
  );
}

export function JoinLinkStatusBadge({
  state,
  className,
}: {
  state: JoinWindowState;
  className?: string;
}) {
  const { label, variant, icon: Icon } = JOIN_WINDOW_META[state];

  return (
    <Badge variant={variant} className={cn('join-link-status', className)} data-status={state}>
      {state === 'open' ? (
        <span className="animate-pulse-dot size-1.5 rounded-full bg-current" aria-hidden />
      ) : (
        <Icon aria-hidden />
      )}
      {label}
    </Badge>
  );
}

export function RecordingStatusBadge({
  state,
  className,
}: {
  state: RecordingState;
  className?: string;
}) {
  const { label, variant, icon: Icon } = RECORDING_STATUS_META[state];

  return (
    <Badge variant={variant} className={cn('recording-status', className)} data-status={state}>
      <Icon aria-hidden />
      {label}
    </Badge>
  );
}

/** Recording mode is configuration rather than state, so it stays monochrome. */
export function RecordingModeLabel({ mode }: { mode: 'auto' | 'manual' | 'off' }) {
  const label = { auto: 'Automatic', manual: 'Manual', off: 'Off' }[mode];
  return <span className="capitalize">{label}</span>;
}

export function StatusDot({ variant, className }: { variant: BadgeVariant; className?: string }) {
  return <span aria-hidden className={cn('size-2 rounded-full', VARIANT_DOT[variant], className)} />;
}
